/** A resolution's colour in `@/lib/base/badges` terms. */
export type ResolutionBadgeColor =
  | 'success'
  | 'error'
  | 'brand'
  | 'gray'
  | 'warning';

export function badgeColorForResolution(
  resolution: string
): ResolutionBadgeColor {
  switch (resolution) {
    case 'approved':
      return 'success';
    case 'rejected':
    case 'failed':
      return 'error';
    case 'executing':
      return 'brand';
    case 'withdrawn':
      return 'gray';
    default:
      return 'warning'; // pending
  }
}

/**
 * True once a proposal is history: approved, rejected, withdrawn, or failed.
 * `pending` and `executing` are live states with something still to happen.
 */
export function isSettledResolution(resolution: string): boolean {
  switch (resolution) {
    case 'approved':
    case 'rejected':
    case 'withdrawn':
    case 'failed':
      return true;
    default:
      return false;
  }
}

export interface ResolutionStyles {
  /**
   * Raw Tailwind classes for a hand-rolled badge span.
   *
   * @deprecated Use `badgeColorForResolution` with `Badge` from
   * `@/lib/base/badges/badges` instead — a hand-rolled span drops the design
   * tokens and dark-mode handling the library badge carries. Two consumers
   * still read this (`MaterialsList`, `ChainResultsPreview`); migrate each when
   * you next touch it, then delete this field.
   */
  badge: string;
  label: string;
}

export function getResolutionStyles(resolution: string): ResolutionStyles {
  switch (resolution) {
    case 'approved':
      return {
        badge:
          'bg-utility-success-50 text-utility-success-700 border border-utility-success-200 dark:bg-utility-success-950 dark:text-utility-success-400 dark:border-utility-success-700',
        label: 'Approved',
      };
    case 'rejected':
      return {
        badge:
          'bg-utility-error-50 text-utility-error-700 border border-utility-error-200 dark:bg-utility-error-950 dark:text-utility-error-400 dark:border-utility-error-700',
        label: 'Dismissed',
      };
    case 'failed':
      return {
        badge:
          'bg-utility-error-50 text-utility-error-700 border border-utility-error-200 dark:bg-utility-error-950 dark:text-utility-error-400 dark:border-utility-error-700',
        label: 'Failed',
      };
    case 'executing':
      return {
        badge:
          'bg-utility-brand-50 text-utility-brand-700 border border-utility-brand-200 dark:bg-utility-brand-950 dark:text-utility-brand-400 dark:border-utility-brand-700',
        label: 'Executing',
      };
    case 'withdrawn':
      return {
        badge:
          'bg-bg-secondary text-fg-tertiary border border-border-secondary',
        label: 'Withdrawn',
      };
    default: // pending
      return {
        badge:
          'bg-utility-warning-50 text-utility-warning-700 border border-utility-warning-200 dark:bg-utility-warning-950 dark:text-utility-warning-400 dark:border-utility-warning-700',
        label: 'Pending',
      };
  }
}
